import { Box, Checkbox, FormControl, Icon, Link, Tag, Text } from "@chakra-ui/react";
import { t } from "i18next";
import { FaLock } from "react-icons/fa";
import { formatNumber } from "../../../../common/formatting/formatNumber";
import { useDisplayDate } from "../../../../common/formatting/useDisplayDate";
import { TipTap } from "../../../../common/input/TipTap/TipTap";
import { useAppSelector } from "../../../../common/redux/hooks";
import { CustomContractDataFieldDto } from "../../../../autogen/bff-api";
import { displayPersonNameWithEmail } from "../../../../common/persons/displayPersonName";

export const CustomField = ({ field }: { field: CustomContractDataFieldDto }) => {
  return (
    <Box pb="4">
      <Text fontWeight={"bold"} fontSize="sm">
        {field.name}
      </Text>
      {field.isInternal && (
        <Text fontSize={"xs"} color="smMuted" pb="2">
          <Icon as={FaLock} boxSize={"2"} mr="1" />
          {t("Internal field - will not be visible to contract counterparty")}
        </Text>
      )}
      <FieldValue field={field} />
    </Box>
  );
};

const FieldValue = ({ field }: { field: CustomContractDataFieldDto }) => {
  const displayDate = useDisplayDate();
  const contractState = useAppSelector((state) => state.contract.state);

  switch (field.type) {
    case "CustomAmount":
      return (
        <Text fontSize="sm">
          {formatNumber(field.amount)} {field.currency ?? contractState?.contract.currency}
        </Text>
      );
    case "CustomText":
      return <Text fontSize="sm">{field.text}</Text>;
    case "CustomNumber":
      return <Text fontSize="sm">{formatNumber(field.value)}</Text>;
    case "CustomUrl":
      return (
        <Link fontSize="sm" target="_blank" rel="noopener noreferrer">
          {field.url}
        </Link>
      );
    case "CustomRichText":
      return <TipTap content={field.text ?? ""} editable={false} />;
    case "CustomEmail":
      return <Text fontSize="sm">{field.email}</Text>;
    case "CustomBoolean":
      return (
        <FormControl>
          <Checkbox pl="4" isChecked={field.isChecked ?? undefined} isDisabled={true} />
        </FormControl>
      );
    case "CustomTag":
      return (
        <Tag fontSize="xs" colorScheme="teal">
          {field.tag}
        </Tag>
      );
    case "CustomDate":
      return <Text>{field.date ? displayDate(field.date) : null}</Text>;
    case "CustomPercentage":
      return (
        <Text fontSize="sm">
          {/* eslint-disable-next-line @typescript-eslint/no-non-null-assertion */}
          {!field.value && field.value !== 0 ? null : field.value * 100}%
        </Text>
      );
    case "CustomPhone":
      return (
        <Text fontSize="sm">
          {field.callingCode} {field.number}
        </Text>
      );
    case "CustomEnum":
      return (
        <Tag fontSize="xs" colorScheme="teal">
          {field.value}
        </Tag>
      );
    case "CustomMultiEnum":
      return (
        <>
          {field.values?.map((value) => (
            <Tag key={value} fontSize="xs" colorScheme="teal" mr="1">
              {value}
            </Tag>
          ))}
        </>
      );
    case "CustomPerson":
      return (
        <Text fontSize="sm">{field.person ? displayPersonNameWithEmail(field.person) : t("No person provided")}</Text>
      );
  }
};
